import { useEffect, useState } from "react";
import type { Rpc } from "../lib/rpc";
import { PLATFORMS } from "../lib/platforms";

interface ScriptState {
  running: boolean;
  startedAt: number | null;
}
interface PlatformStatus {
  status?: string;     // "ok" | "working" | "idle" | "error" | "unknown"
  message?: string;
  timestamp?: string;
}

interface Props {
  rpc: Rpc;
  electronConnected: boolean;
  platform: string | null;
}

export function HeaderBar({ rpc, electronConnected, platform }: Props) {
  const [state, setState] = useState<ScriptState | null>(null);
  const [status, setStatus] = useState<PlatformStatus | null>(null);
  const [busy, setBusy] = useState<null | "start" | "stop" | "post">(null);

  const platformInfo = PLATFORMS.find((p) => p.key === platform);
  // Platforms without an automation script — keep this in sync with which
  // platforms have a manage_*.py wired in PLATFORMS.<key>.script in main.js.
  const canRunScript = platformInfo && platform !== "fansly" && platform !== "gmail";

  // Poll the script + platform status whenever the active platform changes.
  useEffect(() => {
    if (!electronConnected || !platform) {
      setState(null);
      setStatus(null);
      return;
    }
    let cancelled = false;
    async function tick() {
      try {
        const [s, st] = await Promise.all([
          rpc.call<ScriptState>("get-script-state", platform),
          rpc.call<PlatformStatus>("get-status", platform),
        ]);
        if (!cancelled) {
          setState(s);
          setStatus(st);
        }
      } catch { /* swallow */ }
    }
    tick();
    const handle = window.setInterval(tick, 2500);
    return () => { cancelled = true; window.clearInterval(handle); };
  }, [rpc, electronConnected, platform]);

  // Surface script-exited events immediately (don't wait for poll).
  useEffect(() => {
    if (!electronConnected) return;
    const off = rpc.onEvent<{ platform: string; code: number }>("script-exited", (ev) => {
      if (ev.platform === platform) setState({ running: false, startedAt: null });
    });
    return off;
  }, [rpc, electronConnected, platform]);

  async function toggleScript() {
    if (!platform || !state) return;
    const action = state.running ? "stop" : "start";
    setBusy(action);
    try {
      const channel = state.running ? "stop-script" : "start-script";
      await rpc.call(channel, platform);
      // Refresh once for instant feedback.
      const s = await rpc.call<ScriptState>("get-script-state", platform);
      setState(s);
    } catch (err) {
      console.warn("[header] toggle failed", err);
    } finally {
      setBusy(null);
    }
  }

  async function postNow() {
    if (!platform) return;
    setBusy("post");
    try {
      // Look up the active account and enqueue a priority-now job for it.
      // Falls back to the legacy send-command path if accounts.json can't
      // be read (so a misconfigured environment still posts).
      const accts = await rpc.call<{ activeAccountId: string | null }>("get-accounts");
      const accountId = accts?.activeAccountId;
      if (accountId) {
        await rpc.call("jobs-enqueue", {
          platform, accountId, action: "post", priority: "now",
        });
      } else {
        await rpc.call("send-command", { platform, action: "post_now" });
      }
    } catch (err) {
      console.warn("[header] post failed", err);
    } finally {
      setBusy(null);
    }
  }

  if (!platform || !platformInfo) {
    return <div className="header-bar empty">No platform selected</div>;
  }

  return (
    <div className="header-bar">
      <div className="hb-platform">
        <span className="hb-icon" style={{ color: platformInfo.color }}>{platformInfo.icon}</span>
        <span className="hb-name">{platformInfo.name}</span>
      </div>

      <div className="hb-actions">
        {canRunScript && (
          <button
            className={`hb-btn script ${state?.running ? "running" : ""}`}
            onClick={toggleScript}
            disabled={!state || busy !== null || !electronConnected}
          >
            <span className="hb-btn-icon">{state?.running ? "■" : "▶"}</span>
            {busy === "start" ? "Starting…"
              : busy === "stop" ? "Stopping…"
              : state?.running ? "Stop" : "Start"}
          </button>
        )}
        {canRunScript && (
          <button
            className="hb-btn post"
            onClick={postNow}
            disabled={!state?.running || busy !== null || !electronConnected}
            title={!state?.running ? "Start the script first" : "Trigger an immediate post"}
          >
            <span className="hb-btn-icon">📤</span>
            {busy === "post" ? "Sending…" : "Post Now"}
          </button>
        )}
      </div>

      {status && (
        <div className={`hb-status state-${status.status ?? "unknown"}`} title={status.timestamp || ""}>
          <span className="hb-status-dot" />
          <span className="hb-status-text">{status.message || status.status || "—"}</span>
        </div>
      )}
    </div>
  );
}
